import type { ButtonHTMLAttributes, ReactNode, Ref } from "react";
import { Link } from "react-router";
import { cn } from "../../lib/cn";
import { Spinner } from "./spinner";

const variants = {
  primary:
    "bg-brand text-on-brand border-brand hover:bg-brand-hover hover:border-brand-hover hover:shadow-brand active:bg-brand-active",
  secondary: "bg-surface text-ink border-edge-strong hover:bg-surface-2",
  soft: "bg-brand-soft text-brand-fg border-transparent hover:bg-brand-soft-hover",
  outline: "bg-surface text-brand-fg border-blue-300 hover:bg-brand-soft hover:border-blue-400",
  ghost: "bg-transparent text-ink-2 border-transparent hover:bg-surface-2 hover:text-ink",
  danger: "bg-danger text-white border-danger hover:bg-danger-hover hover:border-danger-hover",
} as const;

// Phones get 44px targets, small buttons too; desktop keeps the denser 36px and 30px sizes.
const sizes = {
  sm: "h-11 md:h-[30px] px-3 gap-1.5 text-sm",
  md: "h-11 md:h-9 px-4 gap-2 text-base",
  lg: "h-11 px-5 gap-2 text-base",
  icon: "size-11 md:size-9 p-0",
} as const;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
  /** Shows a spinner, disables the button and announces that work is in progress. */
  loading?: boolean;
  /** Text read out and shown while loading, e.g. "Signing in…". */
  loadingText?: string;
  icon?: ReactNode;
  block?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

function buttonClass(
  variant: keyof typeof variants,
  size: keyof typeof sizes,
  block: boolean,
  className?: string,
) {
  return cn(
    "inline-flex shrink-0 cursor-pointer items-center justify-center whitespace-nowrap rounded-md border font-semibold",
    "transition-[background-color,border-color,box-shadow,color] duration-150 ease-out",
    "disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none",
    variants[variant],
    sizes[size],
    block && "w-full",
    className,
  );
}

/** A link that looks like a button: for actions that open another page ("Add staff member"). */
export function ButtonLink({
  to,
  variant = "primary",
  size = "md",
  icon,
  block = false,
  className,
  children,
}: {
  to: string;
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
  icon?: ReactNode;
  block?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Link to={to} className={buttonClass(variant, size, block, cn("no-underline", className))}>
      {icon}
      {children}
    </Link>
  );
}

export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  loadingText,
  icon,
  block = false,
  disabled,
  className,
  children,
  type = "button",
  ref,
  ...rest
}: ButtonProps) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={buttonClass(variant, size, block, className)}
      {...rest}
    >
      {loading ? <Spinner /> : icon}
      {loading && loadingText ? loadingText : children}
    </button>
  );
}
